import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { aplicarTema, guardarTema, leerTemaGuardado, type Tema } from './tema'

export interface ContextoTema {
  tema: Tema
  cambiarTema: (tema: Tema) => void
}

export const contextoTema = createContext<ContextoTema | null>(null)

/**
 * Mantiene el tema elegido y lo refleja en el atributo del elemento raiz.
 *
 * No escucha `prefers-color-scheme`. Cuando el tema es `sistema`, quien resuelve
 * claro u oscuro es el CSS (`color-scheme: light dark` en `index.css`), asi que
 * el cambio de preferencia del sistema operativo se ve solo, sin `matchMedia` y
 * sin volver a renderizar. Es el mismo argumento que ya daba `graficos/paleta.ts`
 * para publicar la paleta como custom properties.
 *
 * El estado inicial se lee de `localStorage` de forma sincrona, en el
 * inicializador de `useState`, para que el primer render ya sea el bueno.
 */
export function ProveedorTema({ children }: { children: ReactNode }) {
  const [tema, setTema] = useState<Tema>(leerTemaGuardado)

  // El script de `index.html` ya ha puesto el atributo antes de la primera
  // pintura; esto lo mantiene en sintonia con el estado de React y cubre los
  // entornos donde ese script no corre, como las pruebas.
  useEffect(() => {
    aplicarTema(tema)
  }, [tema])

  const cambiarTema = useCallback((siguiente: Tema) => {
    setTema(siguiente)
    guardarTema(siguiente)
  }, [])

  const valor = useMemo(() => ({ tema, cambiarTema }), [tema, cambiarTema])

  return <contextoTema.Provider value={valor}>{children}</contextoTema.Provider>
}
